import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:7207';

class SocketManager {
  public socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  connect(token?: string): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    this.socket = io(SOCKET_URL, {
      auth: {
        token: token || localStorage.getItem('admin_token'),
      },
      transports: ['websocket', 'polling'],
    });

    this.setupEventListeners();
    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.handleReconnect();
    });

    // Admin-specific events
    this.socket.on('new_booking', (data) => {
      console.log('New booking:', data);
      this.emitCustomEvent('new_booking', data);
    });

    this.socket.on('booking_status_update', (data) => {
      console.log('Booking status update:', data);
      this.emitCustomEvent('booking_status_update', data);
    });

    this.socket.on('new_user_registration', (data) => {
      console.log('New user registration:', data);
      this.emitCustomEvent('new_user_registration', data);
    });

    this.socket.on('worker_approval_request', (data) => {
      console.log('Worker approval request:', data);
      this.emitCustomEvent('worker_approval_request', data);
    });

    this.socket.on('new_rating_pending', (data) => {
      console.log('New rating pending:', data);
      this.emitCustomEvent('new_rating_pending', data);
    });

    this.socket.on('new_testimonial_pending', (data) => {
      console.log('New testimonial pending:', data);
      this.emitCustomEvent('new_testimonial_pending', data);
    });

    this.socket.on('system_notification', (data) => {
      console.log('System notification:', data);
      this.emitCustomEvent('system_notification', data);
    });

    // Worker availability updates
    this.socket.on('worker_availability_updated', (data) => {
      console.log('Worker availability updated:', data);
      this.emitCustomEvent('worker_availability_updated', data);
    });
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.connect();
    }, delay);
  }

  private emitCustomEvent(eventName: string, data: any): void {
    // Emit custom events that components can listen to
    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  }

  // Emit events to server
  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  // Listen to custom events
  on(event: string, callback: (data: any) => void): () => void {
    const handler = (e: CustomEvent) => callback(e.detail);
    window.addEventListener(event, handler as EventListener);
    
    return () => {
      window.removeEventListener(event, handler as EventListener);
    };
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const socketManager = new SocketManager();

// React hook for using socket
export const useSocket = () => {
  const socket = socketManager.socket || socketManager.connect();
  const connect = (token?: string) => socketManager.connect(token);
  const disconnect = () => socketManager.disconnect();
  const emit = (event: string, data?: any) => socketManager.emit(event, data);
  const on = (event: string, callback: (data: any) => void) => socketManager.on(event, callback);
  const isConnected = socketManager.isConnected();

  return {
    socket,
    connect,
    disconnect,
    emit,
    on,
    isConnected,
  };
};

export default socketManager;
